import React, { useCallback, useEffect, useMemo } from 'react';
import { DateTime } from 'luxon';
import isEmpty from 'lodash/isEmpty';
import cx from 'classnames';
import { Grid } from '~/components';
import { useForm, useLoadSection } from '~/hooks';
import { stringUtils } from '~/util';
import sectionTypes from '../sectionTypes';
import { ValidationErrorMessage } from '..';
import { get } from 'lodash';

const { hasValue } = stringUtils;

const initialState = {
    sectionId: 0,
    sectionDetails: [],
    selectedDeed: {},
    hasErrors: false
};

function validate(data = {}) {
    let hasError = false;

    const fileNumber = get(data, 'fileNumber', '');
    const commitmentNumber = get(data, 'commitmentNumber', '');

    hasError = hasError 
        || !hasValue(fileNumber) 
        || !hasValue(commitmentNumber);

    if (hasError) return hasError;

    if (!Array.isArray(data.sectionDetails) || data.sectionDetails.length < 1)
        return false;
    
    hasError = data.sectionDetails.reduce(
        (res, d) => res || validateDetail(d),
        false
    );

    return hasError;
}

function validateDetail(detail = {}) {
    const {
        book,
        page,
        countyRegistry
    } = detail._data;

    return !hasValue(book)
        || !hasValue(page)
        || !hasValue(countyRegistry);
}

const Review = ({ titleOpinionId, handleValidation }) => {
    const { onLoad: loadSection } = useLoadSection(sectionTypes.FILE, titleOpinionId);

    const onLoad = useCallback(async (state) => {
        return loadSection(state)
            .then(v => {
                const hasErrors = validate(v);
                return {
                    ...v,
                    hasErrors
                };
            })
            .catch(e => console.error(e));
    }, [loadSection]);

    const { 
        state: { values }, 
        fieldActions 
    } = useForm({ initialState, onLoad });

    const deeds = useMemo(() => {
        return Array.isArray(values.sectionDetails)
            ? values.sectionDetails.map((d,i) => ({ ...d, index: i }) )
            : [];
    }, [values.sectionDetails]);
    
    const selectRow = (data, index) => {
        if (values.selectedDeed && index == values.selectedDeed.index) {
            fieldActions.onChange({}, 'selectedDeed');
        } else {
            return fieldActions.onChange({ ...data, index }, 'selectedDeed');
        }
    };

    useEffect(async () => {
        await handleValidation(values.hasErrors);
    }, [values]);

    return (
        <div className='text-xs text-neutral-90 py-4 grid grid-cols-12 gap-2'>
            { values.hasErrors && (
                <ValidationErrorMessage className='col-span-12'/>
            )}
            <Col span={4} className={cx('flex items-center mb-1', { ['text-error-60']: isEmptyString(values.fileNumber)})}>
                <p className='font-semibold text-neutral-100 mr-6'>Your File No.</p>
                <p>{values.fileNumber}</p>
            </Col>
            <Col span={4} className={cx('col-start-1 flex items-center mb-1', { ['text-error-60']: isEmptyString(values.commitmentNumber)})}>
                <p className='font-semibold text-neutral-100 mr-6'>Supplementing Commitment Number</p>
                <p>{values.commitmentNumber}</p>
            </Col>
            <Col span={12} className='flex flex-col mb-4'>
                <p className='font-semibold text-neutral-100'>Supplementing the Preliminary Opinion on Title, the undersigned certifies all Requirements in Schedule B-I are satisfied except:</p>
                <p>{values.b2Exceptions}</p>
            </Col>
            <Col span={12}>
                <p className='mb-2'>Select a Deed below to view detailed information</p>
                <DataGrid data={deeds} onRowClick={selectRow}/>
            </Col>
            <Col span={12}>
                {!isEmpty(values.selectedDeed) && (<DeedInfo deed={values.selectedDeed} />)}
            </Col>
        </div>
    );
};

export default Review;

const headerClassName = 'bg-gray-50 py-2 px-4 border-b text-left';
const props = {
    className: 'py-1 px-4 border-t text-xs text-neutral-70'
};

function DataGrid({data, onRowClick, className}) {
    const columns = [
        {
            title: 'Deeds',
            canSort: false,
            cell: ({ data }) => {
                const hasErrors = validateDetail(data);

                return (
                    <span className={cx({ ['text-error-60']: hasErrors })}>
                        {`Deed #${data.index + 1}`}
                    </span>
                );
            },
            headerClassName,
            props
        }
    ];

    return (
        <Grid 
            columns={columns} 
            data={data} 
            className={className}
            rowSelect
            events={{
                onRowClick
            }}
        />
    );
}

const currencyFormatter = Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

function DeedInfo ({deed}) {
    const { _data, index } = deed;

    let price = '';
    if (hasValue(_data.purchasePrice)) {
        if (/[^0-9]/i.test(_data.purchasePrice)) {
            price = _data.purchasePrice;
        } else {
            price = currencyFormatter.format(_data.purchasePrice);
        }
    }

    const dated = hasValue(_data.dated)
        ? DateTime.fromISO(_data.dated).toLocal().toLocaleString()
        : '';

    const recordedOn = hasValue(_data.recordedOn)
        ? DateTime.fromISO(_data.recordedOn).toLocal()
        : '';

    const countyError = !hasValue(_data.countyRegistry);
    const bookPageError = !hasValue(_data.book) || !hasValue(_data.page);

    const docs = Array.isArray(deed.documents)
        ? deed.documents
        : [];

    return (
        <div className='grid grid-cols-12 mt-4 gap-2'>
            <Col span={12} className='px-2 border-b border-gray-290 mb-2'>
                <p className='text-sm font-medium'>Deed #{index+1}</p>
            </Col>
            <Col span={12} className='flex flex-col mb-2'>
                <p className='font-semibold text-neutral-100'>Deed Grantor(s)</p>
                <p>{_data.grantors}</p>
            </Col>
            <Col span={12} className='flex flex-col mb-2'>
                <p className='font-semibold text-neutral-100'>Deed Grantee(s)</p>
                <p>{_data.grantees}</p>
            </Col>
            <Col span={4} className='col-start-1 flex items-center mb-1'>
                <p className='font-semibold text-neutral-100 mr-6'>Purchase Price</p>
                <p>{price}</p>
            </Col>
            <Col span={4} className='col-start-1 flex items-center mb-1'>
                <p className='font-semibold text-neutral-100 mr-6'>Dated</p>
                <p>{dated}</p>
            </Col>
            <Col span={4} className='col-start-1 flex items-center mb-1 pr-2'>
                <p className='font-semibold text-neutral-100 mr-4'>Recorded On</p>
                <p>{recordedOn.toLocaleString()}</p>
                <p className='font-semibold text-neutral-100 mx-4'>At</p>
                <p>{recordedOn.toLocaleString(DateTime.TIME_SIMPLE)}</p>
            </Col>
            <Col span={6} className='col-start-1 flex items-center mb-1'>
                <p className={cx('font-semibold mr-4', bookPageError ? 'text-error-60' : 'text-neutral-100')}>Book &amp; Page</p>
                <p>Book {_data.book}, Page {_data.page}</p>
            </Col>
            <Col span={6} className='flex items-center mb-1'>
                <p className={cx('font-semibold mr-4', countyError ? 'text-error-60' : 'text-neutral-100')}>
                    County Registry
                </p>
                <p>{_data.countyRegistry}</p>
            </Col>
            <Col span={6} className='col-start-1 flex items-center mb-1'>
                <p className='font-semibold text-neutral-100 mr-4'>Copy of Deed</p>
                <p>{docs.map((doc, i) => <span key={`deed-copy-${i}`}>{doc.name}</span>)}</p>
            </Col>
        </div>
    );    
} 

function Col({ children, span = 1, className = '' }) {
    return (
        <div className={`col-span-${span} ${className}`}>
            {children}
        </div>
    );
}

function isEmptyString(value) {
    return isEmpty(value) || value.trim().length < 1;
}